import { useState, useEffect } from 'react';
import {
  Layout,
  Table,
  message,
  Spin,
  Pagination,
  Modal,
  Button,
  Form,
  Input,
  Row,
  Col,
  Select,
  AutoComplete,
} from 'antd';

import AdminSidebar from '../components/Sidebar';
import AppHeader from '../components/Header';
import { encryptData, decryptData } from '../middleware/encryption';

const { Content } = Layout;
const { TextArea } = Input;

const PLAN_OPTIONS = [
  { value: 1, label: 'Free' },
  { value: 2, label: 'Basic' },
  { value: 3, label: 'Growth' },
  { value: 4, label: 'Professional' }
];

export default function UserList() {
  const [collapsed, setCollapsed] = useState(false);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [limit] = useState(10);
  const [searchQuery, setSearchQuery] = useState('');
  const [userOptions, setUserOptions] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [planModalVisible, setPlanModalVisible] = useState(false);
  const [updatingPlanId, setUpdatingPlanId] = useState(null);

  const [form] = Form.useForm();

  useEffect(() => {
    fetchUsers(page, searchQuery);
  }, [page, searchQuery]);

  const fetchUserSuggestions = async (query) => {
    if (!query || query.trim().length === 0) {
      setUserOptions([]);
      return;
    }

    try {
      const myHeaders = new Headers();
      const token = sessionStorage.getItem('access_token');

      if (token) {
        myHeaders.append('Authorization', `Bearer ${token}`);
      }

      const url = `https://api.app.exellius.com/api/users/getUsersWithActivePlans?page=1&limit=10&search=${encodeURIComponent(
        query
      )}`;

      const response = await fetch(url, { method: 'GET', headers: myHeaders });
      const result = await response.json();

      if (!result.data) {
        setUserOptions([]);
        return;
      }

      const decrypted = decryptData(result.data);
      const users = decrypted?.users || [];

      const options = users.map((u) => ({
        value: u.email,
        label: `${u.name || u.email} <${u.email}>`,
        user: u,
      }));

      setUserOptions(options);
    } catch (err) {
      console.error('Error fetching user suggestions:', err);
      setUserOptions([]);
    }
  };

  const fetchUsers = async (currentPage, search = '') => {
    setLoading(true);

    try {
      const myHeaders = new Headers();
      const token = sessionStorage.getItem('access_token');

      if (token) {
        myHeaders.append('Authorization', `Bearer ${token}`);
      }

      const requestOptions = {
        method: 'GET',
        headers: myHeaders,
        redirect: 'follow'
      };

      const url = `https://api.app.exellius.com/api/users/getUsersWithActivePlans?page=${currentPage}&limit=${limit}&search=${encodeURIComponent(search)}`;

      const response = await fetch(url, requestOptions);
      const result = await response.json();

      if (result.data) {
        const decryptedResponse = decryptData(result.data);
        console.log('Decrypted user list response:', decryptedResponse);

        setUsers(decryptedResponse.users);
        setTotal(decryptedResponse.pagination.total);
      } else {
        message.error(result.message || 'Failed to fetch users');
      }
    } catch (error) {
      console.error('Error fetching users:', error);
      message.error('An error occurred while fetching users');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (value) => {
    setSearchQuery(value);
    setPage(1);
  };

  const handlePaginationChange = (page) => {
    setPage(page);
  };

  const showModal = (user) => {
    setSelectedUser(user);
    setPlanModalVisible(true);
  };

  const showAddCreditModal = (user) => {
    setSelectedUser(user);
    setIsModalVisible(true);
  };

  const handleCancel = () => {
    setIsModalVisible(false);
    setSelectedUser(null);
    setPlanModalVisible(false);
  };

  const handleAddCreditCancel = () => {
    setIsModalVisible(false);
    form.resetFields();
  };

  const handleAddCreditOk = async () => {
    try {
      const values = await form.validateFields();

      const token = sessionStorage.getItem('access_token');

      const myHeaders = new Headers();
      myHeaders.append('Content-Type', 'application/json');
      myHeaders.append('Authorization', `Bearer ${token}`);

      const final_body = encryptData({
        userId: selectedUser.user_id,
        credits: values.credits,
        comment: values.comment
      });

      const requestOptions = {
        method: 'POST',
        headers: myHeaders,
        body: JSON.stringify({ data: final_body }),
        redirect: 'follow'
      };

      const response = await fetch(
        'https://api.app.exellius.com/api/plans/addCreditsToUser',
        requestOptions
      );

      if (response.ok) {
        fetchUsers(page, searchQuery);

        message.success('Credits added successfully');

        setIsModalVisible(false);

        form.resetFields();
      } else {
        const result = await response.json();

        message.error(result.message || 'Failed to add credits');
      }
    } catch (error) {
      console.error('Error adding credits:', error);

      message.error('An error occurred while adding credits');
    }
  };

  const handlePlanChange = async (userId, planId) => {
    try {
      setUpdatingPlanId(userId);

      const token = sessionStorage.getItem('access_token');

      const myHeaders = new Headers();

      myHeaders.append('Content-Type', 'application/json');
      myHeaders.append('Authorization', `Bearer ${token}`);

      const encryptedBody = encryptData({
        user_id: userId,
        plan_id: planId
      });

      const requestOptions = {
        method: 'POST',
        headers: myHeaders,
        body: JSON.stringify({
          data: encryptedBody
        }),
        redirect: 'follow'
      };

      const response = await fetch(
        'https://api.app.exellius.com/api/plans/updateUserSubscriptionPlan',
        requestOptions
      );

      const result = await response.json();

      if (response.ok) {
        message.success('Plan updated successfully');

        fetchUsers(page, searchQuery);
      } else {
        message.error(result.message || 'Failed to update plan');
      }
    } catch (error) {
      console.error('Error updating plan:', error);

      message.error('An error occurred while updating plan');
    } finally {
      setUpdatingPlanId(null);
    }
  };

  const columns = [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name'
    },

    {
      title: 'Email',
      dataIndex: 'email',
      key: 'email'
    },

    {
      title: 'Credits',
      dataIndex: 'credits',
      key: 'credits'
    },
    {
title:"API use",
dataIndex:"api_credits",
key:"api_credits"
    },

    {
      title: 'Create Date',
      dataIndex: 'user_created_at',
      key: 'user_created_at',
      render: (date) => new Date(date).toLocaleDateString()
    },

    {
  title: 'Plan Type',
  dataIndex: 'plan_type',
  key: 'plan_type',

  render: (_, record) => {
    console.log(record.active_plans[0]?.plan_id);

    return (
      <Select
        style={{ width: 160 }}
        value={Number(record.active_plans[0]?.plan_id) || 1}
        options={PLAN_OPTIONS}
        loading={updatingPlanId === record.user_id}
        onChange={(value) =>
          handlePlanChange(record.user_id, value)
        }
      />
    );
  }
},

    {
      title: 'Plans',
      key: 'plans',

      render: (_, record) => (
        <Button
          type="link"
          onClick={() => showModal(record)}
        >
          View Plans
        </Button>
      )
    },

    {
      title: 'Action',
      key: 'action',

      render: (_, record) => (
        <Button
          type="primary"
          size="small"
          onClick={() => showAddCreditModal(record)}
        >
          Add Credit
        </Button>
      )
    }
  ];

  return (
    <Layout style={{ minHeight: '100vh' }}>
      <AdminSidebar
        collapsed={collapsed}
        onCollapse={setCollapsed}
      />

      <Layout className="site-layout">
        <AppHeader />

        <Content style={{ margin: '24px 16px' }}>
          <div
            style={{
              background: '#fff',
              padding: 24,
              borderRadius: 8,
            }}
          >
            <Row
              justify="space-between"
              align="middle"
              style={{ marginBottom: 20 }}
            >
              <Col span={12}>
                <h2 style={{ margin: 0 }}>
                  Users with Active Plans
                </h2>
              </Col>

              <Col span={8}>
                <AutoComplete
                  options={userOptions}
                  value={searchQuery}
                  onSearch={(val) => fetchUserSuggestions(val)}
                  onSelect={async (value, option) => {
                    setSearchQuery(value);
                    setPage(1);

                    const matchedUser = option?.user || null;

                    if (matchedUser) {
                      // fetch users filtered by the selected email
                      await fetchUsers(1, matchedUser.email);
                    }
                  }}
                  onChange={(val) => {
                    setSearchQuery(val);
                    if (!val) {
                      handleSearch('');
                    }
                  }}
                  style={{ width: '100%' }}
                >
                  <Input.Search
                    placeholder="Search by name or email..."
                    onSearch={handleSearch}
                    enterButton
                    allowClear
                  />
                </AutoComplete>
              </Col>
            </Row>

            {loading ? (
              <Spin
                size="large"
                style={{
                  display: 'block',
                  margin: '50px auto'
                }}
              />
            ) : (
              <>
               <Table
  dataSource={users}
  columns={columns}
  rowKey="user_id"
  pagination={false}
  scroll={{ y: '70vh' }}
/>

                <Pagination
                  total={total}
                  pageSize={limit}
                  current={page}
                  onChange={handlePaginationChange}
                  showSizeChanger={false}
                  style={{
                    textAlign: 'right',
                    marginTop: 20
                  }}
                />
              </>
            )}
          </div>

          <Modal
            title="Active Plans"
            open={planModalVisible}
            onCancel={handleCancel}
            footer={[
              <Button key="close" onClick={handleCancel}>
                Close
              </Button>
            ]}
          >
            {selectedUser?.active_plans?.length > 0 ? (
              selectedUser.active_plans.map((plan) => (
                <div
                  key={plan.subscription_id}
                  style={{
                    marginBottom: 15,
                    borderBottom: '1px solid #f0f0f0',
                    paddingBottom: 10
                  }}
                >
                  <p>
                    <strong>Plan Name:</strong>{' '}
                    {plan.plan_name}
                  </p>

                  <p>
                    <strong>Points:</strong>{' '}
                    {plan.points}
                  </p>

                  <p>
                    <strong>Price:</strong> $
                    {plan.price}
                  </p>

                  <p>
                    <strong>Ends At:</strong>{' '}
                    {new Date(
                      plan.ends_at
                    ).toLocaleString()}
                  </p>
                </div>
              ))
            ) : (
              <p>No active plans found for this user.</p>
            )}
          </Modal>

          <Modal
            title={`Add Credits to ${
              selectedUser?.name || 'User'
            }`}
            open={isModalVisible}
            onOk={handleAddCreditOk}
            onCancel={handleAddCreditCancel}
            okText="Add Credits"
          >
            <Form form={form} layout="vertical">
              <Form.Item
                name="credits"
                label="Amount"
                rules={[
                  {
                    required: true,
                    message: 'Enter amount'
                  }
                ]}
              >
                <Input
                  type="number"
                  placeholder="e.g. 100"
                />
              </Form.Item>

              <Form.Item
                name="comment"
                label="Reason/Comment"
                rules={[
                  {
                    required: true,
                    message: 'Enter a comment'
                  }
                ]}
              >
                <TextArea
                  rows={3}
                  placeholder="Promotional credits, support adjustment, etc."
                />
              </Form.Item>
            </Form>
          </Modal>
        </Content>
      </Layout>
    </Layout>
  );
}